import templateHtml from '@components/templates/footer.html?raw';
import { ensureAdsense } from '@utils/third-party.ts';
import { hasMinimumContent } from '@utils/ads.ts';
import { hydrateIcons } from '@utils/icons.ts';
import type { OnNavigate } from '../types/app.d.ts';

export function Footer({ onNavigate, hideAds = false }: { onNavigate: OnNavigate; hideAds?: boolean }) {
  const footer = document.createElement('footer');
  footer.className = 'footer';

  footer.innerHTML = templateHtml;

  // Hydrate icons (for RSS icon)
  hydrateIcons(footer);

  const adHost = footer.querySelector('[data-ad-slot]') as HTMLElement | null;

  if (adHost && hideAds) {
    adHost.classList.add('hidden');
  }

  // Don't load ads if explicitly disabled (e.g., 404 pages)
  if (adHost && !hideAds) {
    let observer: IntersectionObserver | undefined;

    const showShellAndLoadAd = () => {
      if (!adHost || (adHost as HTMLElement).dataset.loaded === 'true') {
        return;
      }
      adHost.classList.remove('hidden');
      loadAd();
    };

    const loadAd = () => {
      if (!adHost || (adHost as HTMLElement).dataset.loaded === 'true') {
        return;
      }

      (adHost as HTMLElement).dataset.loaded = 'true';

      if (observer) {
        observer.disconnect();
        observer = undefined;
      }

      ensureAdsense().catch(() => {
        // Ignore ads script load failures
      });

      const ad = document.createElement('ins');
      ad.className = 'adsbygoogle footer-ad';
      ad.style.display = 'block';
      ad.setAttribute('data-ad-client', 'ca-pub-3615614508734124');
      ad.setAttribute('data-ad-slot', '4091490183');
      ad.setAttribute('data-ad-format', 'auto');
      ad.setAttribute('data-full-width-responsive', 'true');

      adHost.innerHTML = '';
      adHost.appendChild(ad);

      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {
        // Silently ignore AdSense errors
      }
    };

    const defer = (cb: () => void) => {
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(cb, { timeout: 1500 });
      } else {
        window.setTimeout(cb, 1200);
      }
    };

    const scheduleAd = () => {
      if (!adHost) {
        return;
      }
      // Hide the "Advertisement" placeholder until we are about to load; show only when triggering.
      adHost.classList.add('hidden');

      if (typeof window.IntersectionObserver === 'function') {
        // Observe the footer so we trigger when it nears view (shell is hidden so has no box).
        observer = new IntersectionObserver((entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              showShellAndLoadAd();
              break;
            }
          }
        }, { rootMargin: '0px 0px 200px 0px' });

        observer.observe(footer);
      } else {
        defer(showShellAndLoadAd);
      }
    };

    const primeAd = () => {
      if ((adHost as HTMLElement).dataset.loaded === 'true') {
        return;
      }

      // Check if page has sufficient content before loading ads
      // This prevents ads on empty states, 404 pages, etc.
      const mainContent = document.querySelector<HTMLElement>('main');
      if (!mainContent || !hasMinimumContent(mainContent)) {
        adHost.classList.add('hidden');
        return; // Don't load ads if content is insufficient
      }

      scheduleAd();

      const triggerOnce = () => {
        // Re-check content at trigger time in case page changed
        const mainEl = document.querySelector<HTMLElement>('main');
        if (!mainEl || !hasMinimumContent(mainEl)) {
          adHost.classList.add('hidden');
          return;
        }
        showShellAndLoadAd();
      };

      window.addEventListener('pointerdown', triggerOnce, { once: true });
      window.addEventListener('keydown', triggerOnce, { once: true });
      window.addEventListener('scroll', triggerOnce, { once: true, passive: true });
    };

    footer.addEventListener('adslot:init', () => {
      showShellAndLoadAd();
    }, { once: true });

    if (document.readyState === 'complete') {
      primeAd();
    } else {
      window.addEventListener('load', primeAd, { once: true });
    }
  }

  footer.addEventListener('click', (e) => {
    const t = e.target;
    if (!(t instanceof HTMLElement)) return;

    const navBtn = t.closest('[data-nav]');
    if (navBtn) {
      e.preventDefault();
      const navTarget = navBtn.getAttribute('data-nav');
      if (navTarget) {
        onNavigate(navTarget);
      }
    }
  });

  return footer;
}
